<template>
  <b-card title="Export declaration">
    <b-card-text class="text-right">
      <b-button
        v-b-modal.export-declaration-modal
        v-b-tooltip.hover.top="'Download one AES515 file for each tracking code'"
        variant="outline-success"
      >
        Download xmls
      </b-button>
    </b-card-text>

    <b-modal
      id="export-declaration-modal"
      title="Download xmls"
      ok-title="Download"
      :ok-disabled="!warehouseId || downloading"
      @show="open"
      @ok="download"
    >
      <b-form-group label="Warehouse">
        <b-form-select
          v-model="warehouseId"
          :options="warehouses"
        />
      </b-form-group>
      <p class="mb-0">
        The selected warehouse address is used as the location of goods.
      </p>
      <b-alert
        v-if="failures.length"
        show
        variant="danger"
        class="mt-1 mb-0"
      >
        <p class="mb-1">
          These declarations were not downloaded. Fix them, then try again.
        </p>
        <ul class="mb-0 pl-3">
          <li
            v-for="(failure, index) in failures"
            :key="index"
          >
            {{ failure }}
          </li>
        </ul>
      </b-alert>
    </b-modal>
  </b-card>
</template>
<script>
import {
  BAlert, BButton, BCard, BCardText, BFormGroup, BFormSelect, BModal, VBTooltip,
} from 'bootstrap-vue'
import apiResponse from '@/libs/apiResponse'
import handleError from '@/views/components/errorHandler'
import moment from 'moment/moment'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardText,
    BFormGroup,
    BFormSelect,
    BModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: ['shipmentId', 'companyId'],
  data() {
    return {
      warehouseId: null,
      downloading: false,
      failures: [],
      warehouses: [{ value: null, text: 'Select a warehouse' }],
    }
  },
  methods: {
    open() {
      this.failures = []
      this.loadWarehouses()
    },
    loadWarehouses() {
      this.$http.get(`/v1/companies/${this.companyId}/warehouses`)
        .then(response => {
          const warehouses = (response.data.data || []).filter(
            warehouse => Number(warehouse.company && warehouse.company.id) === Number(this.companyId),
          )
          this.warehouses = [{ value: null, text: 'Select a warehouse' }].concat(
            warehouses.map(warehouse => ({
              value: warehouse.id,
              text: warehouse.name,
            })),
          )
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    async download(event) {
      event.preventDefault()
      if (!this.warehouseId) {
        return
      }
      this.downloading = true
      this.failures = []
      const url = `${process.env.VUE_APP_API_URL}/v1/companies/${this.companyId}/shipments/${this.shipmentId}/export-declaration?warehouse_id=${encodeURIComponent(this.warehouseId)}`
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
          credentials: 'include',
          redirect: 'manual',
        })

        if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
          const redirectUrl = response.headers.get('Location')
          apiResponse.triggerDownload(redirectUrl || url)
          this.$bvModal.hide('export-declaration-modal')
          this.$emit('done')
          return
        }

        if (!response.ok) {
          const json = await response.json().catch(() => ({
            message: 'The export declaration zip could not be downloaded.',
          }))
          const declarations = json.errors && json.errors.declarations
          this.failures = Array.isArray(declarations) ? declarations : []
          handleError({ response: { data: json } }, this.$toast)
          return
        }

        const blob = await response.blob()
        const time = moment().format('YYYY-MM-DD')
        this.startDownload(response, blob, `${time}_export_declaration.zip`)
        this.$bvModal.hide('export-declaration-modal')
        this.$emit('done')
      } catch (error) {
        handleError(error, this.$toast)
      } finally {
        this.downloading = false
      }
    },
    startDownload(response, blob, fallbackFileName = '') {
      const disposition = response.headers.get('content-disposition') || ''
      const filename = disposition.split('filename=')[1] || fallbackFileName
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.setAttribute('download', filename.replace(/"/g, ''))
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(objectUrl)
    },
  },
}
</script>
